import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { isEditOrPreviewMode } from '@/lib/editModeHelpers';
import Link from './Link';
import RichText, { RichTextProps } from '../cms/RichText';
import { useDarkModeContext } from './PDP/DarkModeContext/useDarkModeContext';

interface TextProps extends RichTextProps {}

const ExpandableRichText: React.FC<TextProps> = ({ className, ...props }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsable, setIsCollapsable] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isEditMode = isEditOrPreviewMode();
  const darkmodeContext = useDarkModeContext();
  const isDarkMode = darkmodeContext?.isDarkMode ?? false;

  useEffect(() => {
    if (contentRef.current) {
      const observer = new ResizeObserver(entries => {
        const [entry] = entries;
        if (entry?.target.clientHeight > 0) {
          setIsContentLoaded(true);
          observer.disconnect();
        }
      });

      observer.observe(contentRef.current);
      return () => observer.disconnect();
    }
  }, []);

  const getMaxHeight = useCallback(() => {
    if (!contentRef.current || !isContentLoaded) return 0;

    let maxHeight = 0;
    contentRef.current.classList.add('expandable-line-clamp');
    maxHeight = contentRef.current.clientHeight;
    contentRef.current.classList.remove('expandable-line-clamp');
    return maxHeight;
  }, [isContentLoaded]);

  const setMaxHeight = useCallback(() => {
    if (!wrapperRef.current || !contentRef.current || !isContentLoaded || isEditMode) return;

    const maxHeight = getMaxHeight();
    setIsCollapsable(contentRef.current.clientHeight > maxHeight);

    if (!isExpanded) {
      wrapperRef.current.style.maxHeight = `${maxHeight}px`;
    } else {
      wrapperRef.current?.style.removeProperty('max-height');
    }
  }, [getMaxHeight, isEditMode, isExpanded, isContentLoaded]);

  useEffect(() => {
    if (isContentLoaded) {
      setMaxHeight();
      window.addEventListener('resize', setMaxHeight);
      return () => window.removeEventListener('resize', setMaxHeight);
    }
  }, [setMaxHeight, isContentLoaded]);

  return (
    <div className={classnames(className)} data-component={'ExpandableRichText'}>
      <div ref={wrapperRef} className="overflow-hidden">
        <RichText ref={contentRef} {...props} />
      </div>
      {isCollapsable && (
        <div className="mt-6 lg:mt-8">
          <Link
            as="button"
            text={isExpanded ? 'See less' : 'See more'}
            variant="expand-button"
            icon={isExpanded ? 'chevron-up' : 'chevron-down'}
            onClick={() => setIsExpanded(!isExpanded)}
            className={isDarkMode ? 'text-white' : ''}
          />
        </div>
      )}
    </div>
  );
};

export default ExpandableRichText;
